import React, { forwardRef, useState } from "react";
import { func, object, string } from "prop-types";
import DatePicker, { registerLocale, setDefaultLocale } from "react-datepicker";
import { bn, enUS } from "date-fns/locale";
import { connect } from "react-redux";
import { range } from "lodash";
import { addDays, endOfDay, getMonth, getYear, startOfDay, subDays } from "date-fns";

import FilterInput from "../FilterInput";
import Icons from "../../icons";

import { toBanglaMonth, toBanglaString, toEngMonth } from "../../utils/utils";
import { staticTexts } from "../../utils/Translate";

import "./filter-date-range-picker.m.css";

const FilterDateRangePicker = ({ className = "", query = {}, lang, navigateToPage = () => {} }) => {
  const [dateRange, setDateRange] = useState([
    query["published-after"] ? new Date(Number(query["published-after"])) : null,
    query["published-before"] ? new Date(Number(query["published-before"])) : null,
  ]);
  const [startDate, endDate] = dateRange;
  const years = range(1998, getYear(new Date()) + 1, 1);
  const locals = { bn: { text: "bn", local: bn }, en: "en-US", local: enUS };

  registerLocale(locals[lang].text, locals[lang].local);
  setDefaultLocale(lang === "bn" ? "bn" : "en-US");

  const header = ({
    date,
    changeYear,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }) => {
    return (
      <div styleName="date-picker-header">
        <button styleName="datepicker-header-buttons" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
          <Icons type="back-icon" styleName="arrow-left" />
        </button>

        <div styleName="header-center">
          <label htmlFor="year-dropdown" styleName="month-name">
            {lang === "bn" ? toBanglaMonth(getMonth(date) + 1) : toEngMonth(getMonth(date) + 1)}
          </label>

          <select
            styleName="year-dropdown"
            value={getYear(date)}
            id="year-dropdown"
            onChange={({ target: { value } }) => changeYear(value)}
          >
            {years.map((option) => (
              <option styleName="year-item" key={option} value={option}>
                {lang === "bn" ? toBanglaString(option) : option}
              </option>
            ))}
          </select>
        </div>

        <button styleName="datepicker-header-buttons" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
          <Icons type="back-icon" styleName="arrow-right" />
        </button>
      </div>
    );
  };

  const day = (day) => {
    return (
      <div styleName="day" className="day">
        {lang === "bn" ? toBanglaString(day) : day}
      </div>
    );
  };

  const onClear = () => {
    setDateRange([]);

    if (!(query["published-before"] || query["published-after"])) return null;

    delete query["published-before"];
    delete query["published-after"];

    const queryString = new URLSearchParams(query).toString();
    navigateToPage(`/search?${queryString}`);
  };

  // eslint-disable-next-line react/display-name, react/prop-types
  const Input = forwardRef(({ value, onClick }, ref) => (
    <FilterInput
      type="text"
      onInputClick={onClick}
      onClear={onClear}
      isClear={true}
      styleName="date-range-input"
      placeHolder={staticTexts(lang).date}
      iconName="calendar"
      id="datepicker-input"
      ref={ref}
      value={lang === "bn" ? toBanglaString(value) : value}
    />
  ));

  const search = (update) => {
    const [start, end] = update;

    if (start && end) {
      query["published-before"] = endOfDay(end).getTime();
      query["published-after"] = startOfDay(start).getTime();
    } else {
      delete query["published-before"];
      delete query["published-after"];
    }

    const queryString = new URLSearchParams(query).toString();
    navigateToPage(`/search?${queryString}`);
  };

  return (
    <div className={className} styleName="base">
      <div styleName="date-picker">
        <DatePicker
          selectsRange={true}
          title={false}
          showPopperArrow={false}
          startDate={startDate}
          endDate={endDate}
          locale={locals[lang].text}
          renderDayContents={day}
          renderCustomHeader={header}
          maxDate={addDays(new Date(), 0)}
          minDate={subDays(new Date(1998, 10, 4), 0)}
          onChange={(update) => {
            setDateRange(update);
            const [start, end] = update;

            if ((start && end) || (!start && !end)) {
              search(update);
            }
          }}
          dateFormat="dd/MM/yyyy"
          showDisabledMonthNavigation={true}
          customInput={<Input />}
        />
      </div>
    </div>
  );
};

FilterDateRangePicker.propTypes = {
  text: string,
  className: string,
  type: string,
  lang: string,
  query: object,
  navigateToPage: func,
};

const mapDispatchToProps = (dispatch) => ({
  navigateToPage: (url) => global.app.navigateToPage(dispatch, url),
});

export default connect(null, mapDispatchToProps)(FilterDateRangePicker);
